<script setup lang="ts">
import { ref } from 'vue'
import { validateEmail, forgotPassword } from '../services/authService'
import { useUiStore } from '../stores/uiStore'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'close'): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const uiStore = useUiStore()

const email = ref('')
const emailError = ref('')
const successMsg = ref('')
const isLoading = ref(false)

const resetForm = () => {
  email.value = ''
  emailError.value = ''
  successMsg.value = ''
}

const handleSubmit = async () => {
  emailError.value = ''
  successMsg.value = ''

  if (!email.value) {
    emailError.value = '此欄不可留白'
    return
  }

  if (!validateEmail(email.value)) {
    emailError.value = '請輸入有效的電子信箱'
    return
  }

  isLoading.value = true

  const result = await forgotPassword(email.value)

  if (result.success) {
    successMsg.value = '密碼重置郵件已發送，請至信箱查看（1小時內有效）'
  } else {
    emailError.value = result.error || '申請失敗'
  }

  isLoading.value = false
}

const handleCancel = () => {
  resetForm()
  emit('close')
}

const errorFontSize = Math.max(8, Math.floor(uiStore.fontSize / 2))
</script>

<template>
  <div v-if="isOpen" class="forgot-password-overlay">
    <div class="forgot-password-dialog" @click.stop>
      <div class="dialog-header">
        <h2>忘記密碼</h2>
        <button class="close-btn" @click="handleCancel">×</button>
      </div>

      <div class="dialog-content">
        <div class="form-group">
          <label>電子信箱</label>
          <input
            v-model="email"
            type="email"
            placeholder="輸入您的電子信箱"
            class="form-input"
            :disabled="isLoading || !!successMsg"
          />
          <div v-if="emailError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ emailError }}
          </div>
        </div>

        <div v-if="successMsg" class="success-text">{{ successMsg }}</div>

        <div v-if="!successMsg" class="button-group">
          <button
            class="dialog-btn primary"
            @click="handleSubmit"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isLoading ? '發送中...' : '發送' }}
          </button>
          <button
            class="dialog-btn secondary"
            @click="handleCancel"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            取消
          </button>
        </div>

        <div v-if="successMsg" class="close-btn-group">
          <button
            class="dialog-btn secondary"
            @click="handleCancel"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.forgot-password-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 330;
}

.forgot-password-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.dialog-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #1f2937;
}

.dialog-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  margin-top: 4px;
  font-size: 12px;
}

.success-text {
  color: #10b981;
  margin-bottom: 16px;
  padding: 12px;
  background: #ecfdf5;
  border-radius: 4px;
  font-size: 14px;
}

.button-group {
  display: flex;
  gap: 8px;
}

.close-btn-group {
  display: flex;
}

.dialog-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.dialog-btn.primary {
  background: #3b82f6;
  color: white;
}

.dialog-btn.primary:hover:not(:disabled) {
  background: #2563eb;
}

.dialog-btn.secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.dialog-btn.secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.dialog-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  color: white;
}

.dialog-btn.secondary:disabled {
  background: #d1d5db;
  color: #6b7280;
}
</style>
